import React from 'react';
import PropTypes from 'prop-types';

import { Page, Text, View, Image, Document, StyleSheet } from '@react-pdf/renderer';

import bankLogo from 'affiliateImages/bank.png';

import PdfStyles from './PdfStyles';
import PdfFooter from '../../../shared/components/Pdf/PdfFooter';
import { showAccountNumber, showCode } from '../../../shared/helper/commonBankDetails';
import PdfHeader from '../../../shared/components/Pdf/PdfHeader';

const InvoicePdfDocument = ({ pdfDetails }) => {
  const styles = StyleSheet.create(PdfStyles);
  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <PdfHeader
          welltravelName={pdfDetails.welltravelName}
          welltravelAddress1={pdfDetails.welltravelAddress1}
          welltravelAddress2={pdfDetails.welltravelAddress2}
          welltravelVat={pdfDetails.welltravelVat}
        />
        <View style={styles.subheader}>
          <View style={styles.invoiceInfo}>
            <Text style={styles.invoiceID}>{pdfDetails.title}</Text>
          </View>
        </View>

        <View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.invoiceToLabel}>{pdfDetails.to}</Text>
            <Text style={styles.invoiceData}>
              {pdfDetails.clientName} {pdfDetails.clientAddress}
            </Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.issueDateLabel}>{pdfDetails.issueDate}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.createdAt}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.issuedByLabel}>{pdfDetails.issueBy}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.welltravelName}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.generatedByLabel}>{pdfDetails.generatedBy}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.generatedByExecutor}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.transactionMethodLabel}>{pdfDetails.transactionMethod}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.transactionVia}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.tableDataFirst}>{pdfDetails.tableHeaderItem}</Text>
            {pdfDetails.reason && (
              <Text style={styles.tableDataFirst}>
                {I18n.t('components.billing.invoices.remarks.title')}
              </Text>
            )}
            <Text style={styles.tableDataLast}>{pdfDetails.tableHeaderAmount}</Text>
          </View>
          <View style={styles.tableDataRow}>
            <Text style={styles.tableDataFirst}>
              {pdfDetails.reason ? pdfDetails.lineItemTitle : pdfDetails.lineItem}
            </Text>
            {pdfDetails.reason && (
              <Text style={styles.tableDataFirst}>
                {I18n.t(
                  `billing.welltravel_boooking.transactions.emphasis.${
                    pdfDetails.reason === 'subscription_charge'
                      ? 'subscription_charge'
                      : 'price_increased'
                  }`,
                )}
              </Text>
            )}
            <Text style={styles.tableDataLastAmount}>{pdfDetails.amount}</Text>
          </View>
          <View style={styles.tableSubTotalDataRow}>
            <Text style={styles.tableSubTotalData}>{pdfDetails.tableHeaderSubtotal}</Text>
            <Text style={styles.tableDataLastAmount}>{pdfDetails.amount}</Text>
          </View>
          <View style={styles.line} />
          <View style={styles.tableTotalDataRow}>
            <Text style={styles.tableTotalData}>{pdfDetails.tableTotal}</Text>
            <Text style={styles.tableTotalAmount}>{pdfDetails.amount}</Text>
          </View>
        </View>

        {!pdfDetails.isSubscriptionPdf && !pdfDetails.reason && (
          <View style={styles.description}>
            <Text style={styles.descriptionLabel}>{pdfDetails.descriptionLabel}</Text>
            <Text style={styles.descriptionData}>{pdfDetails.description}</Text>
          </View>
        )}

        {!pdfDetails.reason && (
          <View style={styles.payment} fixed={true}>
            <Text style={styles.paymentInstruction}>{pdfDetails.paymentInstruction}</Text>
            <View style={styles.paymentDetails}>
              <View style={styles.bankDetails}>
                <View style={styles.currencyInfo}>
                  <Image style={styles.image} src={bankLogo} />
                  <View style={styles.currency}>
                    <Text style={styles.currencyCode}>{pdfDetails.currency}</Text>
                    <Text style={styles.currencyName}>{pdfDetails.bankDetails.currencyName}</Text>
                  </View>
                </View>

                <View>
                  <Text style={styles.Iban}>{showAccountNumber(pdfDetails.bankDetails)}</Text>
                  <Text style={styles.bankName}>
                    {I18n.t('components.billing.invoices.bank_account_details.bank', {
                      name: pdfDetails.bankDetails.bankName,
                    })}
                  </Text>
                  <Text style={styles.bankBIC}>{showCode(pdfDetails.bankDetails)}</Text>
                  <Text style={styles.beneficiary}>
                    {I18n.t('components.billing.invoices.bank_account_details.beneficiary', {
                      beneficiary: pdfDetails.bankDetails.beneficiary,
                    })}
                  </Text>
                  <Text style={styles.beneficiaryAddress}>
                    {I18n.t(
                      'components.billing.invoices.bank_account_details.beneficiary_address',
                      {
                        beneficiary_address: pdfDetails.bankDetails.beneficiaryAddress,
                      },
                    )}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        )}

        <PdfFooter footerSupport={pdfDetails.footerSupport} page={pdfDetails.page} />
      </Page>
    </Document>
  );
};

InvoicePdfDocument.propTypes = {
  pdfDetails: PropTypes.shape({
    affiliateName: PropTypes.string.isRequired,
    welltravelName: PropTypes.string.isRequired,
    welltravelAddress1: PropTypes.string.isRequired,
    welltravelAddress2: PropTypes.string.isRequired,
    welltravelVat: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    clientAddress: PropTypes.string.isRequired,
    issueDate: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    issueBy: PropTypes.string.isRequired,
    clientNameLabel: PropTypes.string.isRequired,
    generatedBy: PropTypes.string.isRequired,
    transactionMethod: PropTypes.string.isRequired,
    tableHeaderAmount: PropTypes.string.isRequired,
    tableHeaderVat: PropTypes.string.isRequired,
    tableHeaderSubtotal: PropTypes.string.isRequired,
    tableHeaderItem: PropTypes.string.isRequired,
    tableTotal: PropTypes.string.isRequired,
    descriptionLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    lineItem: PropTypes.string.isRequired,
    lineItemTitle: PropTypes.string,
    amount: PropTypes.string.isRequired,
    vat: PropTypes.string.isRequired,
    total: PropTypes.string.isRequired,
    paymentInstruction: PropTypes.string.isRequired,
    page: PropTypes.string.isRequired,
    footerSupport: PropTypes.string.isRequired,
    footerWebsite: PropTypes.string.isRequired,
    generatedByExecutor: PropTypes.string.isRequired,
    transactionVia: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    isSubscriptionPdf: PropTypes.string.isRequired,
    bankDetails: PropTypes.shape({
      bankName: PropTypes.string.isRequired,
      currencyName: PropTypes.string.isRequired,
      iban: PropTypes.string,
      bic: PropTypes.string,
      sort_code: PropTypes.string,
      beneficiary: PropTypes.string.isRequired,
      beneficiaryAddress: PropTypes.string.isRequired,
    }).isRequired,
    reason: PropTypes.string,
  }).isRequired,
};

export default InvoicePdfDocument;
